<template>
  <div>
    <button
      v-if="hasOnlyOneAction"
      @click="tryPerformAction(orderedActions[0])"
      type="button"
      :disabled="isPerformingAction || isInEditMode"
      class="btn action-btn green"
    >
      <template v-if="generatingDocument">
        <xpinner :has-background="false" color="White" speed="fast"/>
      </template>
      {{orderedActions[0].actionName}}
    </button>
    <button
      v-else-if="orderedActions.length > 1"
      :disabled="isPerformingAction || isInEditMode"
      class="dropdown-toggle arrow btn action-btn green"
      id="actionsDropdown"
      data-toggle="dropdown"
      aria-haspopup="true"
      aria-expanded="false"
    >
      <template v-if="generatingDocument">
        <xpinner :has-background="false" color="White" speed="fast"/>
      </template>
      Actions
    </button>

    <ul class="dropdown-menu dropdown-menu-right uncover" aria-labelledby="actionsDropdown">
      <li v-for="action in orderedActions" :key="action.action_client_id">
        <button
          :disabled="isPerformingAction || isInEditMode"
          type="button"
          class="dropdown-item"
          @click="tryPerformAction(action)"
        >{{action.actionName}}</button>
      </li>
    </ul>

    <action-confirmation-modal
      v-if="modalStateFor(vueModalID.confirmObjectModelAction).isVisible"
      :state="modalStateFor(vueModalID.confirmObjectModelAction)"
      :index="vueModalID.confirmObjectModelAction"
      @toggle-modal="handleCloseConfirmationModal"
      :overrideConfig="actionConfirmationModalConfig"
      :entry="selectedObjectModelEntry"
      :context="NotesTabContext.Records"
      :note-required="selectedObjectModelAction.require_note"
      :action-title="selectedObjectModelAction.label_title"
    ></action-confirmation-modal>

    <multiple-submissions-modal
      v-if="modalStateFor(vueModalID.multipleSubmissions).isVisible"
      :state="modalStateFor(vueModalID.multipleSubmissions)"
      :index="vueModalID.multipleSubmissions"
      @start-form="startForm"
      @edit-form="editForm"
      @toggle-modal="hideModal(vueModalID.multipleSubmissions)">
    ></multiple-submissions-modal>

    <object-model-action-error-modal
      :state="modalStateFor(vueModalID.objectModelActionError)"
      :index="vueModalID.objectModelActionError"
      @toggle-modal="handleCloseErrorModal"
      :error-title="actionErrorTitle"
      :error-message="actionErrorMessage"
    ></object-model-action-error-modal>

    <object-model-action-custom-notification
      v-if="!isPreview"
      :state="modalStateFor(vueModalID.objectModelActionCustomNotification)"
      :index="vueModalID.objectModelActionCustomNotification"
      :entry="selectedObjectModelEntry"
      @toggle-modal="handleCloseCustomNotificationModal"
      @send="sendCustomNotification(selectedObjectModelEntry, $event)"
    ></object-model-action-custom-notification>

    <process-flow-submission-app
      v-if="modalStateFor(vueModalID.ProcessFlowSubmissionApp).isVisible"
      :state="modalStateFor(vueModalID.ProcessFlowSubmissionApp)"
      :index="vueModalID.ProcessFlowSubmissionApp"
      :process-flow="processFlow"
      :process-flow-submission-id="processFlowSubmissionId"
      @toggle-modal="processFlowExecutionOnCancel"
      @finished="processFlowExecutionOnFinished"
      @failed="processFlowExecutionOnFailed"
    />
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import selectedMixin from "../mixins/selectedMixin";
import objectModelActionConfirmationMixin from "../mixins/objectModelActionConfirmationMixin";
import ObjectModelActionErrorModal from "./object-model-action-error-modal.vue";
import ObjectModelActionCustomNotification from "./object-model-action-custom-notification.vue";
import objectModelActionPerformMixin from "../mixins/objectModelActionPerformMixin";
import ContentElementComponent from "./content-element-component.vue";
import {NotesTabContext} from "../../formSubmission/enums/notesTabContext";
import {ObjectModelActionType} from "~/enums/objectModelActionType";
import {ObjectModelDefaultActionType} from "~/enums/objectModelDefaultActionType";
import {RouteHelper} from "~/helpers/routeHelper";
import {Route} from "~/enums/route";
import { IObjectModelActionViewReference } from '~/interfaces/contentManagementInterfaces';

export default Vue.extend({
  extends:ContentElementComponent,
  mixins: [
    selectedMixin,
    objectModelActionConfirmationMixin,
    objectModelActionPerformMixin
  ],
  components: {
    ObjectModelActionErrorModal,
    ObjectModelActionCustomNotification,
    ProcessFlowSubmissionApp: () => import("~/../formability/processFlowSubmission/app_vue.vue"),
  },
  computed: {
    objectModel() {
      return this.selectedDetailView.published_object_model ?? this.selectedDetailView.object_model;
    },
    actionReferencesArray(): IObjectModelActionViewReference[] {
      return this.contentElement.contentElementsArray;
    },
    orderedActions() {
      if (!this.selectedPublishedDetailView) return this.contentElement.contentElementsArray;
      return this.contentElement.contentElementsArray.filter(actionElement => {
        const action = this.objectModel.findObjectModelAction(actionElement.action_client_id);
        return this.$can('enable', action, {entry: this.selectedObjectModelEntry, contentView: this.selectedPublishedDetailView})
      });
    },
    hasOnlyOneAction() {
      return this.orderedActions.length === 1;
    },
    objectModelEntryEditFrom(): object {
      return { from: "Detail View", from_detail_view: this.selectedPublishedDetailView.detail_view_id };
    },
    actionPerformContext(): object {
      return {type: "detail_view", id: this.selectedPublishedDetailView.detail_view_id};
    }
  },
  data: function (): object {
    return {
      NotesTabContext
    };
  },
  methods: {
    performSelectedAction(): void {
      if (!this.selectedPublishedDetailView || !this.selectedObjectModelAction) return;

      const objectModelAction = this.selectedObjectModelAction;
      if(objectModelAction.action_type === ObjectModelActionType.DefaultAction && objectModelAction.default_action_type === ObjectModelDefaultActionType.Edit){
        const params = this.$route.params;
        window.location.href = RouteHelper.path({
          route: Route.detailViewEditMode,
          query: { flash: "You are in Edit Mode", flashType: 'edit', tab: this.selectedDetailView.tabs[this.selectedDetailView.current_tab_index].toLowerCase() || this.selectedDetailView.current_tab_index + 1, from_list_view: this.$route.query.from_list_view, from: this.$route.query.from},
          namedWildcardOptions: [
            {wildcard: ":detail_view_id", modelOrId: params.id},
            {wildcard: ":entry_id", modelOrId: params.entry_id }
          ],
        })
      }else{
        this.performAction(objectModelAction, this.selectedObjectModelEntry);
      }
    },
    refreshPage() {
      this.hideModal(this.vueModalID.recordsUpdated);
      window.location.reload();
    }
  }
});
</script>
